import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link2, Loader2, Search } from "lucide-react";
import { adminApi, formatApiError } from "@/context/AuthContext";

const btn = "inline-flex items-center gap-2 border border-white/30 text-white uppercase text-xs font-bold tracking-[0.15em] px-4 py-2.5 hover:border-white transition-colors disabled:opacity-50";

// Finds the podcast's show on the LGTDZ Mixcloud account from its title and puts the right player in the form
export const MixcloudPicker = ({ title, date, onPick }) => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.get("/admin/podcasts/mixcloud", { params: { title, date: date || undefined } });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="mixcloud-picker">
      <button type="button" onClick={search} disabled={busy || (title || "").trim().length < 2} data-testid="mixcloud-search-btn" className={btn}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Trouver le player Mixcloud d'après le titre
      </button>
      {items && items.length === 0 && <p className="text-sm text-zinc-400" data-testid="mixcloud-empty">Aucune émission trouvée sur le Mixcloud LGTDZ.</p>}
      {items?.length > 0 && (
        <ul className="border border-white/10 divide-y divide-white/10" data-testid="mixcloud-results">
          {items.map((c, i) => (
            <li key={c.url} className="flex items-center gap-3 p-2" data-testid={`mixcloud-result-${i}`}>
              {c.picture ? <img src={c.picture} alt="" className="w-10 h-10 object-cover shrink-0" /> : <span className="w-10 h-10 bg-white/5 shrink-0" />}
              <div className="min-w-0 flex-1">
                <a href={c.url} target="_blank" rel="noreferrer" className="block text-sm text-white truncate hover:text-primary">{c.name}</a>
                <p className={`text-[11px] uppercase tracking-[0.15em] font-bold ${c.score >= 0.8 ? "text-emerald-400" : "text-zinc-400"}`}>
                  Ressemblance {Math.round(c.score * 100)} %
                </p>
              </div>
              <button type="button" onClick={() => { onPick(c.widget); setItems(null); toast.success("Player ajouté : enregistre pour valider."); }}
                data-testid={`mixcloud-use-${i}`} className="bg-white text-black uppercase text-[11px] font-bold tracking-[0.15em] px-3 py-2 hover:bg-primary hover:text-white transition-colors">
                Utiliser
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Admin > Podcasts: links every podcast without a player to its Mixcloud show
export const MixcloudAutolink = () => {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/podcasts/mixcloud-autolink");
      qc.invalidateQueries({ queryKey: ["/podcasts"] });
      toast.success(`${data.linked.length} podcast(s) reli\u00e9(s) \u00e0 Mixcloud${data.missing.length ? ` \u00b7 ${data.missing.length} \u00e0 choisir \u00e0 la main` : ""}.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={run} disabled={busy} data-testid="mixcloud-autolink-btn" className={`${btn} mb-6`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Relier les players Mixcloud manquants
    </button>
  );
};
